import {
  isActiveElementTypeable,
  isEscape,
  keysToModifyInQWERTZ,
  LAYOUT_QWERTZ,
} from 'shared/helpers/KeyboardHelpers';
import { useDetectKeyboardLayout } from 'dashboard/composables/useDetectKeyboardLayout';
import { createKeybindingsHandler } from 'tinykeys';
import { onUnmounted, onMounted, watch, unref, isRef } from 'vue';

/**
 * 判断当前键盘事件是否应被忽略（例如焦点在可输入元素上且未允许穿透）。
 * @param {Event} e
 * @param {Object|Function} handler
 * @returns {boolean}
 */
const shouldIgnoreEvent = (e, handler) => {
  const isTypeable = isActiveElementTypeable(e);
  const allowOnFocusedInput =
    typeof handler === 'function' ? false : handler.allowOnFocusedInput;

  if (isTypeable) {
    if (isEscape(e)) {
      e.target.blur();
    }
    return !allowOnFocusedInput;
  }
  return false;
};

/**
 * 包装处理器：先做忽略判断，再执行真正的 action。
 * @param {Function|Object} handler
 * @returns {Function}
 */
const keydownWrapper = handler => {
  return e => {
    if (shouldIgnoreEvent(e, handler)) return;

    const actionToPerform =
      typeof handler === 'function' ? handler : handler.action;
    actionToPerform(e);
  };
};

/**
 * 按当前键盘布局包装事件表（QWERTZ 下部分组合需加 Shift 前缀）。
 * @param {Object} events
 * @returns {Promise<Object>}
 */
async function wrapEventsInKeybindingsHandler(events) {
  const wrappedEvents = {};
  const currentLayout = await useDetectKeyboardLayout();

  Object.keys(events).forEach(originalEventName => {
    const modifiedEventName =
      currentLayout === LAYOUT_QWERTZ &&
      keysToModifyInQWERTZ.has(originalEventName)
        ? `Shift+${originalEventName}`
        : originalEventName;

    wrappedEvents[modifiedEventName] = keydownWrapper(
      events[originalEventName]
    );
  });
  return wrappedEvents;
}

/**
 * 全局键盘快捷键绑定 composable。
 * 支持普通对象，或响应式源（ref/computed）；响应式变更时会卸载旧监听并重新绑定。
 * @param {Object|import('vue').Ref} keyboardEvents tinykeys 事件表
 */
export async function useKeyboardEvents(keyboardEvents) {
  let abortController = new AbortController();
  /** 用于丢弃过期的异步 bind（布局检测完成前又触发了新一轮绑定） */
  let bindGeneration = 0;

  /** 解包 ref/computed，得到当前事件表 */
  const resolveEvents = () => unref(keyboardEvents);

  /** 取消旧监听并按当前事件表重新挂载 document keydown */
  const bind = async () => {
    const generation = ++bindGeneration;
    abortController.abort();
    abortController = new AbortController();

    const events = resolveEvents();
    if (!events || !Object.keys(events).length) return;

    const wrappedEvents = await wrapEventsInKeybindingsHandler(events);
    // 等待布局检测期间若已有更新的 bind，则丢弃本次结果
    if (generation !== bindGeneration) return;

    const keydownHandler = createKeybindingsHandler(wrappedEvents);
    document.addEventListener('keydown', keydownHandler, {
      signal: abortController.signal,
    });
  };

  onMounted(async () => {
    await bind();

    if (isRef(keyboardEvents)) {
      watch(keyboardEvents, () => bind(), { deep: true });
    }
  });

  onUnmounted(() => {
    bindGeneration += 1;
    abortController.abort();
  });
}
